import { storeExpenseCategoryData } from "@/Api/expenseCategories";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ApiDataProps } from "@/pages/Tenants";
import { ExpenseCategoryProps } from "@/types/expenseCategory";
import { Check, Loader, PlusCircle } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Form } from "@/components/Form";
import { useToastNavigate } from "@/utils/toastNavigate";

const schema = z.object({
  name: z.string().min(1, "O nome é obrigatório"),
});

type SchemaType = z.infer<typeof schema>;

export function AddExpenseCategoryBtn({
  updateFront,
  dataApi,
}: {
  updateFront: (data: ApiDataProps<ExpenseCategoryProps>) => void;
  dataApi: ApiDataProps<ExpenseCategoryProps>;
}) {
  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");
  const [isOpen, setIsOpen] = useState(false);

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");
    const payload = {
      ...data,
    };

    const { data: apiResponse, ok } = await storeExpenseCategoryData(payload);
    if (!ok) {
      setFormState("error");

      setTimeout(() => {
        setFormState("default");
      }, 1000);
      setIsOpen(false);
      return;
    }

    const dataUpdated = {
      ...dataApi,
      data: [apiResponse?.response, ...dataApi.data],
    };
    updateFront(dataUpdated);
    
    toast({
      description:
        apiResponse?.message || "Categoria de despesa cadastrada com sucesso!",
      action: <Check color="#1edc2b" />,
    });

    form.reset();
    setFormState("default");
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <PlusCircle className="h-4 w-4" /> Nova Categoria
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Adicionar Categoria de Despesa
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        <Form
          onSubmit={form.handleSubmit(formSubmit)}
          {...form}
          className="space-y-3"
        >
          <Form.Row>
            <Form.Field name="name">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Nome <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} placeholder="Insira um nome" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <DialogFooter>
            <Button
              type="submit"
              className={cn("w-full", colors.bg_primary)}
              disabled={formState !== "default"}
            >
              {formState === "loading" && (
                <>
                  Cadastrando <Loader className="ml-2 h-4 w-4 animate-spin" />
                </>
              )}
              {formState === "default" && "Cadastrar"}
              {formState === "error" && "Erro ao cadastrar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}